// The shell's nav vocabulary and its single resolver. Sidebar,
// CommandPalette and RouteAnnouncer all read the same resolved list, so
// the three can never disagree about which screens exist for the org
// and role at hand — the palette used to offer "Contratistas" to a
// facility gerente whose sidebar (correctly) never showed it, and the
// announcer used to fall back to a raw pathname for a route the sidebar
// named. `resolveNavItems` is pure (recipe + session + counts in, items
// out) so the gating can be tested without React; `useVisibleNavItems`
// is the hook the three components call.
//
// Every gate here is a recipe capability or a role-set predicate —
// never recipe.slug (§0.4(6), ADR-0072).

import { useMemo } from "react";
import {
  isIaChatRole,
  isManagementAnalyticsRole,
  isReadOnlyRole,
  isTeamRosterReadRole,
  type RecipeMeResponse,
} from "@bitacorapro/api-types";
import { useFacilityDashboard, useRecipe } from "./api/queries";
import type { Session } from "./api/session";
import { useSession } from "./auth/useSession";
import {
  canImportUnits,
  canManageChecklistTemplates,
  isFacilityDashboardEnabled,
  lacksDashboardSurface,
} from "./capabilities";
import { threadWords } from "./postventa/words";
import type { Icon } from "../components/icons";

// The sidebar's headings. `cartera` only exists at resolution time: a
// group-rooted lifecycle vertical (postventa v2) frames its global
// entries as the cartera level the contextual project block sits above,
// so Contratistas / Enlaces / AI Insights move there from their static
// group. Facility never sees it — its two groups stay Operación /
// Gestión, untouched.
export type NavGroup = "principal" | "cartera" | "gestion";

export type NavItem = {
  key: string;
  label: string;
  path: string;
  icon: keyof typeof Icon;
  group: NavGroup;
  alert?: boolean;
  // Only shown when the org's recipe has a non-empty
  // observationStatuses vocabulary (ADR-0058) — never gated on
  // recipe.slug (§0.4(6)).
  requiresObservationLifecycle?: boolean;
  // Only shown to org-scoped write roles (ADR-0079 Decision 7) — the
  // Configuración screen's checklist-template panel 403s server-side
  // for store_manager/brand_manager on the read too, so surfacing the
  // nav entry for them would just be a dead link into an empty page.
  requiresChecklistTemplateAdmin?: boolean;
  // Only shown to IA_CHAT_ROLE_KEYS (ADR-0139) — the chat is role-scoped
  // server-side, so a facility_supervisor sees its own stores' facts;
  // roles outside the set 403 at POST /ia/chat. The entry also carries
  // `requiresFacilityDashboard`: POST /ia/chat answers VERTICAL_REQUIRED
  // to any non-facility recipe, and the screen's copy and prompt speak
  // of tiendas, so a postventa gerente/jefe would see a dead entry.
  requiresIaChat?: boolean;
  // Gated on TEAM_ROSTER_READ_ROLE_KEYS (product decision 2026-09-03,
  // amends ADR-0089). ADR-0089 originally shipped this nav item hidden
  // from facility_supervisor as a conservative safe default "pending a
  // product decision" — that decision arrived and reversed it:
  // supervisors are the ones who onboard jefes de tienda, and they are
  // already inviter-eligible, so the roster is their working surface,
  // not a courtesy read. The same pass narrowed the other end —
  // store_manager is now excluded too: a jefe manages one store's
  // operation, not the org's people, and isn't inviter-eligible either,
  // so there is no action behind the read for them, only every
  // colleague's email and activity heartbeat.
  requiresTeamRosterRead?: boolean;
  // Sólo para un rol de alcance organizacional. Un rol acotado a un
  // proyecto (jefe de proyecto, `scopeType: "asset_group"`) administra la
  // postventa de su proyecto, no las cuentas ni la receta de la
  // inmobiliaria — el handoff del panel los sacó del sidebar por eso
  // (iteración 2026-09-16). Se gatea por el primitivo de alcance de la
  // receta y no por la clave del rol, así que una vertical que mañana
  // declare otro rol acotado hereda la regla sin tocar este archivo.
  requiresOrgScope?: boolean;
  // Only shown when the recipe enables the facility dashboard — the
  // org-wide open-findings list is that dashboard's working surface. A
  // lifecycle vertical (postventa) has no org-wide observations screen
  // any more: the panel postventa v2 (decisión 4 del 2026-09-29) retired
  // the «Por revisar» queue, and /observaciones now forwards to the
  // active project's observations (ObservacionesRoute). The contextual
  // project sidebar that replaces it is a separate slice.
  requiresFacilityDashboard?: boolean;
  // ADR-0144 §2 — the unit importer. Recipe declares `unitProfile` and
  // the caller may import (canImportUnits): the gerente de postventa,
  // the org admin, or a platform admin.
  requiresUnitImport?: boolean;
  // Hidden from read-only roles (READ_ONLY_ROLE_KEYS — brand_manager in
  // v1, founder 2026-10-19, ADR-0164): core-api answers 403
  // ROLE_READ_ONLY to every write they send. Only for screens whose job
  // is a write, or whose read is denied to that role anyway — a screen
  // with useful read content stays and hides its controls instead.
  hiddenForReadOnly?: boolean;
};

// A nav entry after resolution: its badge is attached, and the label /
// path are already the ones this org sees (not the pre-recipe fallback).
export type VisibleNavItem = NavItem & {
  // null = no badge (not a misleading 0).
  count: number | null;
};

export const NAV_ITEMS: NavItem[] = [
  {
    key: "overview",
    label: "Dashboard",
    path: "/",
    icon: "Home",
    group: "principal",
  },
  // "Tiendas" is the facility default; group-rooted non-facility
  // verticals (postventa) get "Proyectos"/`/proyectos` at render time
  // — see the override in resolveNavItems() below. Keep this literal as
  // the pre-recipe-load fallback only: a loaded recipe with no dashboard
  // surface gets no nav at all (resolveNavItems returns []).
  { key: "tiendas", label: "Tiendas", path: "/tiendas", icon: "Store", group: "principal" },
  // Facility's always-visible open-findings entry, badge = open findings
  // across the caller's stores. Keyed on the dashboards capability,
  // never on the slug; absent for every other vertical. `alert` keeps
  // the red badge facility has always had (open findings are the
  // working queue, not a neutral count).
  {
    key: "observaciones",
    label: "Observaciones",
    path: "/observaciones",
    icon: "Inbox",
    group: "principal",
    alert: true,
    requiresFacilityDashboard: true,
  },
  {
    key: "ia",
    label: "AI Insights",
    path: "/ia",
    icon: "Sparkles",
    group: "principal",
    requiresIaChat: true,
    requiresFacilityDashboard: true,
  },
  // No `requiresOrgScope` here: TEAM_ROSTER_READ_ROLE_KEYS already
  // admits facility_supervisor (asset_group scope) and brand_manager
  // (brand scope) on purpose — they onboard their own people — and the
  // route is gated by RequireTeamRosterRead in App.tsx. An org-scope
  // gate on top of the role set silently hid the roster from exactly the
  // roles the product decision let in.
  {
    key: "equipo",
    label: "Equipo",
    path: "/equipo",
    icon: "Users",
    group: "gestion",
    requiresTeamRosterRead: true,
  },
  {
    // La nómina vive en Gestión y sólo para verticales con ciclo de
    // observaciones: es la que alimenta el drawer de asignación, y sin
    // ese ciclo no hay a quién asignarle nada.
    key: "contratistas",
    label: "Contratistas",
    path: "/contratistas",
    icon: "Users",
    group: "gestion",
    requiresObservationLifecycle: true,
    // The roster only feeds the assignment drawer, and GET /providers is
    // limited to admin and the assigning_party lane — a read-only role
    // declares no lane, so the screen would only show an error.
    hiddenForReadOnly: true,
  },
  {
    // Managing public links (revoke, access history) is the screen's
    // job; a read-only role has neither, only an org-wide list of
    // recipients outside its own scope.
    key: "enlaces",
    label: "Enlaces compartidos",
    path: "/enlaces",
    icon: "External",
    group: "gestion",
    requiresObservationLifecycle: true,
    hiddenForReadOnly: true,
  },
  {
    key: "carga-unidades",
    label: "Cargar unidades",
    path: "/carga-unidades",
    icon: "Units",
    group: "gestion",
    requiresUnitImport: true,
  },
  {
    // ADR-0144 §4 — documentos del proyecto (manual, reglamento,
    // planos). Mismo gating que el importador: misma persona, misma
    // receta (canImportUnits).
    key: "documentos",
    label: "Documentos",
    path: "/documentos",
    icon: "Folder",
    group: "gestion",
    requiresUnitImport: true,
  },
  {
    key: "configuracion",
    label: "Configuración",
    path: "/configuracion",
    icon: "Settings",
    group: "gestion",
    requiresChecklistTemplateAdmin: true,
    requiresOrgScope: true,
    hiddenForReadOnly: true,
  },
];

export type ResolveNavItemsInput = {
  recipe: RecipeMeResponse | undefined;
  session: Session | null;
  // Facility aggregate badges. null while not loaded or not applicable.
  facilityCounts: { assets: number | null; openFindings: number | null };
};

// Route match for an entry that may carry a querystring: active is
// decided by the path.
export function navItemBasePath(item: Pick<NavItem, "path">): string {
  return item.path.split("?")[0]!;
}

// Whether the shell frames its nav around the current project (panel
// postventa v2, README §Navegación): a group-rooted vertical with an
// observation lifecycle and no facility dashboard. Same predicate that
// relabels Tiendas → Proyectos below, plus the lifecycle capability the
// project-scoped Observaciones entry needs — never recipe.slug (§0.4(6)).
export function hasContextualProjectNav(recipe: RecipeMeResponse | undefined): boolean {
  const hasObservationLifecycle = (recipe?.observationStatuses.length ?? 0) > 0;
  const isGroupRooted = recipe?.rootEntity === "group";
  return hasObservationLifecycle && isGroupRooted && !isFacilityDashboardEnabled(recipe);
}

// ADR-0154 — quién ve el análisis de cartera con IA: los roles del tablero
// gerencial en una vertical con proyectos y ciclo de observaciones. El
// servidor además exige que la receta traiga el addendum del memo y
// responde 403 si no; la pantalla lo muestra en vez de esconderlo.
export function canUsePortfolioAnalysis(
  role: string | null | undefined,
  recipe: RecipeMeResponse | undefined
): boolean {
  return isManagementAnalyticsRole(role ?? "") && hasContextualProjectNav(recipe);
}

// Heading copy per group. Pure vocabulary: which group an entry lands in
// is decided by resolveNavItems, so the label needs no recipe.
export function navGroupLabel(group: NavGroup): string {
  switch (group) {
    case "principal":
      return "Operación";
    case "cartera":
      return "Cartera";
    case "gestion":
      return "Gestión";
  }
}

// The entries a contextual-nav vertical lists under «Cartera», in the
// order the handoff shows them (Contratistas · Enlaces compartidos · AI
// Insights). Everything else keeps its static group.
const CARTERA_KEYS = ["contratistas", "enlaces", "ia"];

export function resolveNavItems(input: ResolveNavItemsInput): VisibleNavItem[] {
  const { recipe, session, facilityCounts } = input;
  // A loaded recipe with no dashboard surface (resolveHomeSurface ===
  // "none" — an asset-rooted vertical without `dashboards`, e.g.
  // residential_rental) has no screen to list: every entry would be a
  // dead link into facility vocabulary. The shell renders
  // NoDashboardSurface instead. A still-loading recipe (undefined) is not
  // "none" and keeps the pre-recipe fallback below.
  if (lacksDashboardSurface(recipe)) return [];
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const hasObservationLifecycle = (recipe?.observationStatuses.length ?? 0) > 0;
  const isGroupRooted = recipe?.rootEntity === "group";
  const role = session?.organization.role;
  const canManageChecklists = canManageChecklistTemplates(role);
  const canUseIaChat = isIaChatRole(role ?? "");
  const canReadTeamRoster = isTeamRosterReadRole(role ?? "");
  const canImport = canImportUnits(recipe, role, session?.user.platformAdmin);
  const readOnly = isReadOnlyRole(role ?? "");
  // El alcance del rol sale de la receta, no de su clave. Un rol que la
  // receta no declara cae en organizacional, que es el default histórico
  // y el único seguro: esconder Gestión por no encontrar la declaración
  // sería peor que mostrarla.
  const callerScopeType =
    (recipe?.memberRoles ?? []).find((r) => r.key === role)?.scopeType ?? "organization";
  const isOrgScoped = callerScopeType === "organization";

  // "Overview" instantly redirects to /proyectos for a group-rooted,
  // non-facility vertical (OverviewRoute) — a nav row that redirects to
  // the item right below it is a dead row, so drop it rather than ship it.
  // Configuración is hidden from the nav for every vertical and role
  // (founder decision 2026-10-22); its route and screen are kept on
  // purpose, reachable by URL.
  const navItems = NAV_ITEMS.filter(
    (n) => !(n.key === "overview" && isGroupRooted && !facilityEnabled) && n.key !== "configuracion"
  ).map((n) => {
    if (n.key === "tiendas" && isGroupRooted && !facilityEnabled) {
      return { ...n, label: recipe.labels.assetGroupPlural, path: "/proyectos" };
    }
    // ADR-0134: facility also assigns by link, so it reaches the roster
    // too — under its own noun («Proveedores»), see lib/postventa/words.
    if (n.key === "contratistas" && recipe) {
      return { ...n, label: threadWords(recipe).assigneePlural };
    }
    return n;
  });

  const gated = navItems.filter(
    (n) =>
      (!n.requiresObservationLifecycle || hasObservationLifecycle) &&
      (!n.requiresChecklistTemplateAdmin || canManageChecklists) &&
      (!n.requiresIaChat || canUseIaChat) &&
      (!n.requiresTeamRosterRead || canReadTeamRoster) &&
      (!n.requiresOrgScope || isOrgScoped || session?.user.platformAdmin === true) &&
      (!n.requiresFacilityDashboard || facilityEnabled) &&
      (!n.requiresUnitImport || canImport) &&
      (!n.hiddenForReadOnly || !readOnly)
  );

  // Postventa v2 sidebar: the global entries regroup under «Cartera»,
  // ordered as the handoff lists them; Proyectos stays in `principal`
  // (it is what the project block replaces inside a project) and
  // Equipo / Configuración keep «Gestión» with their current gates
  // (decisión 8 del 2026-09-29). Facility keeps the static groups.
  const visible = hasContextualProjectNav(recipe)
    ? gated
        .map((n) => (CARTERA_KEYS.includes(n.key) ? { ...n, group: "cartera" as const } : n))
        .map((n, i) => ({ n, i }))
        .sort((a, b) => carteraRank(a.n) - carteraRank(b.n) || a.i - b.i)
        .map(({ n }) => n)
    : gated;

  // Counts stay null (no badge, not a misleading 0) until the facility
  // aggregate is actually gated in — non-facility verticals get their
  // own counts source once the cartera surface lands.
  const counts: Record<string, number | null> = {
    tiendas: facilityEnabled ? facilityCounts.assets : null,
    observaciones: facilityEnabled ? facilityCounts.openFindings : null,
  };

  return visible.map((n) => ({ ...n, count: counts[n.key] ?? null }));
}

function carteraRank(n: NavItem): number {
  if (n.group === "principal") return 0;
  if (n.group === "cartera") return 10 + CARTERA_KEYS.indexOf(n.key);
  return 20;
}

// The one hook the shell's three nav consumers call. The facility query
// is gated on the recipe capability it serves — TanStack Query's
// `enabled` is per-observer (see Shell.tsx), so each consumer repeating
// the same gate is what keeps a non-facility org from firing a doomed
// /facility-dashboard request.
export function useVisibleNavItems(): VisibleNavItem[] {
  const session = useSession();
  const { data: recipe } = useRecipe();
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const { data: facility } = useFacilityDashboard(facilityEnabled);
  const assets = facilityEnabled ? (facility?.totals.assets ?? null) : null;
  const openFindings = facilityEnabled
    ? (facility?.byAsset.reduce((sum, a) => sum + a.openFindingsCount, 0) ?? null)
    : null;

  return useMemo(
    () =>
      resolveNavItems({
        recipe,
        session,
        facilityCounts: { assets, openFindings },
      }),
    [recipe, session, assets, openFindings]
  );
}
